/**
 * Google Tag Manager Utilities
 * Helper functions for interacting with GTM
 * GDPR Compliant - GTM only loads after user consent
 */

/**
 * GTM Configuration
 */
const GTM_CONFIG = {
    containerId: "GTM-MW2JC26G",
    loaded: false,
    consentUpdated: false,
};

/**
 * Initialize GTM infrastructure (dataLayer, gtag function)
 * This sets up the basics without loading GTM tracking
 */
function initializeGtmInfrastructure() {
    if (typeof window.dataLayer === "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };

        // Set default consent to denied for GDPR compliance
        window.gtag("consent", "default", {
            ad_user_data: "denied",
            ad_personalization: "denied",
            ad_storage: "denied",
            analytics_storage: "denied",
        });

        console.log("GTM infrastructure initialized with denied consent");
    }
}

/**
 * Initialize GTM infrastructure on page load (GDPR compliant)
 * This only sets up the infrastructure without tracking
 */
export function initializeGtm() {
    initializeGtmInfrastructure();
}

/**
 * Load GTM script only when consent is granted
 * This is GDPR compliant as it only loads when user explicitly consents
 */
function loadGtmScript() {
    if (GTM_CONFIG.loaded) {
        console.log("GTM already loaded");
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        try {
            // Initialize infrastructure first
            initializeGtmInfrastructure();

            // Push GTM start event to dataLayer
            window.dataLayer.push({
                "gtm.start": new Date().getTime(),
                event: "gtm.js",
            });

            // Create and inject GTM script
            const script = document.createElement("script");
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONFIG.containerId}`;

            script.onload = () => {
                GTM_CONFIG.loaded = true;
                console.log("GTM script loaded successfully");

                // Also inject noscript fallback
                injectGtmNoscript();

                resolve();
            };

            script.onerror = () => {
                console.error("Failed to load GTM script");
                reject(new Error("Failed to load GTM script"));
            };

            // Insert script before first script tag
            const firstScript = document.getElementsByTagName("script")[0];
            firstScript.parentNode.insertBefore(script, firstScript);
        } catch (error) {
            console.error("Error loading GTM:", error);
            reject(error);
        }
    });
}

/**
 * Inject GTM noscript fallback for users with JavaScript disabled
 */
function injectGtmNoscript() {
    if (!document.querySelector("noscript[data-gtm-noscript]")) {
        const noscript = document.createElement("noscript");
        noscript.setAttribute("data-gtm-noscript", "true");
        noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_CONFIG.containerId}"
             height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        document.body.insertBefore(noscript, document.body.firstChild);
    }
}

/**
 * Load GTM if consent is granted and not already loaded
 * This is the main function called by the consent component
 */
export function loadGtmIfConsented() {
    console.log("GTM load requested - checking consent status");

    if (!GTM_CONFIG.loaded) {
        return loadGtmScript()
            .then(() => {
                console.log("GTM loaded successfully with user consent");
                return true;
            })
            .catch((error) => {
                console.error("Failed to load GTM:", error);
                return false;
            });
    } else {
        console.log("GTM already loaded");
        return Promise.resolve(true);
    }
}

/**
 * Check if GTM is loaded and available
 * @returns {boolean}
 */
export function isGtmAvailable() {
    return (
        typeof window.gtag === "function" &&
        typeof window.dataLayer !== "undefined" &&
        Array.isArray(window.dataLayer)
    );
}
/**
 * Send a custom event to GTM
 * @param {string} eventName - The event name
 * @param {object} eventParams - Event parameters
 */
export function sendGtmEvent(eventName, eventParams = {}) {
    if (isGtmAvailable()) {
        window.gtag("event", eventName, eventParams);
        console.log("GTM event sent:", eventName, eventParams);
    } else {
        console.warn(
            "GTM not available, event not sent:",
            eventName,
            eventParams
        );
    }
}

/**
 * Update GTM consent settings
 * @param {object} consentSettings - Consent settings object
 * @param {boolean} logConsent - Whether to log consent change for audit trail
 */
export function updateGtmConsent(consentSettings, logConsent = false) {
    if (isGtmAvailable()) {
        window.gtag("consent", "update", consentSettings);
        console.log("GTM consent updated:", consentSettings);

        if (logConsent) {
            // Send consent change event for audit trail (GDPR requirement)
            sendGtmEvent("consent_update", {
                event_category: "privacy",
                consent_settings: JSON.stringify(consentSettings),
                timestamp: new Date().toISOString(),
            });
        }
    } else {
        console.warn(
            "GTM not available, consent not updated:",
            consentSettings
        );
    }
}

/**
 * Send page view event to GTM
 * @param {string} pagePath - The page path
 * @param {string} pageTitle - The page title
 */
export function sendPageView(pagePath, pageTitle = document.title) {
    if (isGtmAvailable()) {
        window.gtag("config", "GTM-MW2JC26G", {
            page_path: pagePath,
            page_title: pageTitle,
        });
        console.log("GTM page view sent:", pagePath, pageTitle);
    }
}
